import { useCallback, useEffect, useState } from "react";
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectAccountData } from "../redux/main/accountSlice";

export const CustomerSelect = ({ selectedOption, setSelectedOption, value, onChange, onSystemSelect, initialSystems = [], showSelectedLabel = true, excludeProspects = false }) => {
    // Supporto per entrambi i modelli di props: selectedOption/setSelectedOption e value/onChange
    const actualSelectedOption = selectedOption || value;
    const actualSetSelectedOption = setSelectedOption || onChange;
    // Inizializza il sistemas con i sistemi iniziali passati dal componente padre
    const [systems, setSystems] = useState(initialSystems);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Inizializza selectedSystem con il valore salvato, se esiste
    const [selectedSystem, setSelectedSystem] = useState(() => {
        // Verifica se c'è un sistema già selezionato da mostrare
        if (initialSystems.length > 0) {
            // Cerca il sistema che corrisponde a quello memorizzato in localStorage o all'id salvato
            const systemId = localStorage.getItem('systemId');
            if (systemId) {
                const matchingSystem = initialSystems.find(sys => sys._id === systemId);
                if (matchingSystem) {
                    // Notifica il componente padre del sistema selezionato
                    if (onSystemSelect && typeof onSystemSelect === 'function') {
                        setTimeout(() => onSystemSelect(matchingSystem), 0);
                    }
                    return matchingSystem;
                }
            }
        }
        return null;
    });
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const accountId = accountData?._id;

    // Funzione per eseguire la chiamata API
    const fetchOptions = async (searchText, pageNum = 0, append = false) => {
        if (!accountId) return;
        
        setIsLoading(true);
        try {
            // Utilizziamo l'endpoint getForSelect2 come richiesto
            let url = `${import.meta.env.VITE_API_BASE_URL}/customers/getForSelect2?accountId=${accountId}&searchTerm=${encodeURIComponent(searchText || '')}&page=${pageNum}&pagination=50`;
            if (excludeProspects) {
                url += '&excludeProspects=true';
            }
            const headers = {
                'Content-Type': 'application/json',
                'auth-token': token
            };
            const options = {
                method: 'GET',
                headers
            };
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Errore nella richiesta: ${errorData.message || 'Errore sconosciuto.'}`)
            };
            const data = await response.json();
            
            // Trasformo i dati nel formato richiesto da React-Select
            // Il backend già prepara text e title in base a business/non-business
            const formattedOptions = data.results?.map((item) => {
                // Usiamo il campo text che il backend ha già preparato correttamente
                // il backend usa item.business ? item.company : `${item.name} ${item.surname}`
                return {
                    value: item.id,
                    label: item.text,  // Il backend già prepara questo campo correttamente
                    data: item
                };
            }) || [];

            // Se append è true, aggiungiamo i nuovi risultati a quelli esistenti
            if (append) {
                setOptions(prevOptions => [...prevOptions, ...formattedOptions]);
            } else {
                setOptions(formattedOptions);
            }

            // Aggiorniamo hasMore in base al numero di risultati ricevuti
            setHasMore(formattedOptions.length === 50);
            setPage(pageNum);
        } catch (error) {
            console.error('Errore durante il recupero dei clienti:', error);
            if (!append) {
                setOptions([]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Carica i primi clienti all'avvio per mostrarli quando si apre il menu
    useEffect(() => {
        if (accountId) {
            fetchOptions('', 0);
        }
    }, [accountId]);

    // Creo una versione debounced della funzione fetchOptions per la ricerca
    const debouncedFetchOptions = useCallback(
        debounce((searchText) => {
            fetchOptions(searchText, 0);
        }, 300),
        [accountId, token]
    );

    // Gestisco l'input utente
    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        // Se l'utente sta cercando, usa la ricerca debounced
        if (newValue && newValue.length > 0) {
            debouncedFetchOptions(newValue);
        } else {
            // Se l'input è vuoto, mostra i primi clienti
            fetchOptions('', 0);
        }
    };

    // Pulizia effetto quando il componente si smonta
    useEffect(() => {
        return () => {
            debouncedFetchOptions.cancel();
        };
    }, [debouncedFetchOptions]);

    // Gestisco la selezione di un elemento dal menu a tendina
    const handleSelect = (selectedItem) => {
        if (selectedItem) {
            // Determina il customerLabel in base al valore di business
            const customerLabel = selectedItem.data.business 
                ? selectedItem.data.company 
                : `${selectedItem.data.name || ''} ${selectedItem.data.surname || ''}`.trim();
            
            // Crea un nuovo oggetto con customerId e customerLabel
            const enrichedSelectedItem = {
                ...selectedItem,
                customerId: selectedItem.value,
                customerLabel: customerLabel
            };
            
            actualSetSelectedOption(enrichedSelectedItem);
            
            // Carica i sistemi associati al cliente selezionato
            fetchSystems(selectedItem.value);
        } else {
            actualSetSelectedOption(null);
            setSystems([]);
        }
    };
    
    // Funzione per caricare i sistemi associati al cliente selezionato
    const fetchSystems = async (customerId) => {
        if (!customerId || !accountId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/systems/systemsList?accountId=${accountId}&customerId=${customerId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error('Errore nel caricamento degli impianti');
            }
            
            const data = await response.json();
            setSystems(data || []);
        } catch (err) {
            console.error('Errore durante il recupero degli impianti:', err);
            setError('Si è verificato un errore durante il caricamento degli impianti. Riprova più tardi.');
        } finally {
            setLoading(false);
        }
    };
    
    // Gestisce la selezione di un sistema
    const handleSystemSelect = (system) => {
        setSelectedSystem(system);
        
        // Salva le variabili richieste in memoria
        if (system) {
            // Salva systemId, systemLabel e systemTypes in localStorage
            localStorage.setItem('systemId', system._id);
            localStorage.setItem('systemLabel', system.title || '');
            localStorage.setItem('systemTypes', JSON.stringify(system.typesList || []));
        } else {
            // Se nessun sistema è selezionato, rimuovi le variabili da localStorage
            localStorage.removeItem('systemId');
            localStorage.removeItem('systemLabel');
            localStorage.removeItem('systemTypes');
        }
        
        // Notifica il componente padre della selezione del sistema
        if (onSystemSelect) {
            onSystemSelect(system);
        }
    };

    // Gestisco lo scroll per caricare più risultati
    const handleMenuScrollToBottom = () => {
        if (!isLoading && hasMore) {
            fetchOptions(inputValue, page + 1, true);
        }
    };

    // Gestisco l'apertura del menu per caricare i primi clienti
    const handleMenuOpen = () => {
        // Se non ci sono opzioni e non c'è una ricerca in corso, carica i primi clienti
        if (options.length === 0 && !inputValue) {
            fetchOptions('', 0);
        }
    };

    return (
        <div className="w-full">
            <Select
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleSelect}
                options={options}
                isLoading={isLoading}
                value={actualSelectedOption}
                placeholder="Cerca cliente..."
                noOptionsMessage={() => inputValue && inputValue.length < 3 ? "Digita almeno 3 caratteri per cercare" : "Nessun cliente trovato"}
                isClearable
                onMenuOpen={handleMenuOpen}
                onMenuScrollToBottom={handleMenuScrollToBottom}
                styles={{
                    option: (baseStyles, state) => ({
                        ...baseStyles,
                        backgroundColor: state.isFocused ? '#fafafa' : state.isSelected ? '#e5e5e5' : 'white',
                        color: '#525252',
                        ':active': {
                            backgroundColor: '#e5e5e5'
                        }
                    }),
                    control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? '#737373' : '#d4d4d4',
                        boxShadow: state.isFocused ? '0 0 0 1px #737373' : 'none',
                        '&:hover': {
                            borderColor: '#737373'
                        }
                    })
                }}
                classNames={{
                    container: () => 'w-full',
                }}
            />
            
            {/* Mostra il valore di customerLabel quando un cliente è selezionato e showSelectedLabel è true */}
            {selectedOption && showSelectedLabel && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <p className="font-medium">Customer Label: {selectedOption.customerLabel}</p>
                </div>
            )}
            
            {/* Nota: La visualizzazione dei sistemi è stata spostata nel componente ClienteStep */}
        </div>
    );
};

export default CustomerSelect;
